// @ts-nocheck - TODO: Update to Chakra UI v3 API
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { ArrowDown } from '@carbon/icons-react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  Flex,
  HStack,
  Input,
  Menu,
  Portal,
  RadioGroup,
  Separator,
  Stack,
  Text,
} from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import logo from '@/assets/hivemq-bee-neg.svg'
import { aiApi } from '@/lib/api'

const Header = ({ isConnected, messagesPerSecond }) => {
  return (
    <Flex w="100%">
      <Avatar.Root size="lg">
        <Avatar.Image src={logo} />
        <Avatar.Fallback name="Logo" />
      </Avatar.Root>
      <Flex flexDirection="column" mx="5" justify="center" gap={1}>
        <Text fontSize="lg" fontWeight="bold">
          Operations CoPilot
        </Text>
        <HStack gap={3}>
          <Badge colorPalette={isConnected ? 'green' : 'red'} variant="subtle" px={2} py={1}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
          <Text fontSize="sm" color="fg.muted">
            {messagesPerSecond ?? 0} msg/s
          </Text>
        </HStack>
      </Flex>
    </Flex>
  )
}

const TypingIndicator = () => (
  <Flex w="100%">
    <Avatar.Root bg="blue.300" mr={2}>
      <Avatar.Image src={logo} />
      <Avatar.Fallback name="Computer" />
    </Avatar.Root>
    <Flex bg="bg.subtle" color="fg" minW="100px" my="1" p="3" align="center">
      <Flex gap="1.5" align="center" px={2}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            w="8px"
            h="8px"
            borderRadius="full"
            bg="fg.muted"
            style={{
              animation: `typingBounce 1.4s ease-in-out ${i * 0.2}s infinite`,
            }}
          />
        ))}
      </Flex>
      <style>{`
				@keyframes typingBounce {
					0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
					30% { transform: translateY(-6px); opacity: 1; }
				}
			`}</style>
    </Flex>
  </Flex>
)

const Messages = ({ messages, downloadChat, resetChat, debugChat, isLoading }) => {
  const _AlwaysScrollToBottom = () => {
    const elementRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
      if (elementRef.current) {
        elementRef.current.scrollIntoView()
      }
    })
    return <div ref={elementRef} />
  }

  return (
    <>
      <Flex w="100%" flexDirection="row" mr="2" mb="2">
        <Flex w="100%" justify="flex-end" mt="2">
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button>
                Actions <ArrowDown />
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item value="download" onClick={downloadChat}>
                    Download
                  </Menu.Item>
                  <Menu.Item value="reset" onClick={resetChat}>
                    Reset Chat
                  </Menu.Item>
                  <Menu.Item value="debug" onClick={debugChat}>
                    Download debug logs
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </Flex>
      </Flex>
      <Flex w="100%" flex="1" minH={0} overflowY="scroll" flexDirection="column" p="2">
        {messages.map((item, index) => {
          if (item.from === 'me') {
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              <Flex key={index} w="100%" justify="flex-end">
                <Flex bg="black" color="white" minW="100px" maxW="1050px" my="1" p="3">
                  <Text>{item.text}</Text>
                </Flex>
              </Flex>
            )
            // biome-ignore lint/style/noUselessElse: <explanation>
          } else {
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              <Flex key={index} w="100%">
                <Avatar.Root bg="blue.300" mr={2}>
                  <Avatar.Image src={logo} />
                  <Avatar.Fallback name="Computer" />
                </Avatar.Root>
                <Flex bg="bg.subtle" color="fg" minW="100px" maxW="1050px" my="1" p="3">
                  <Box textAlign="left" marginLeft={5}>
                    <Markdown remarkPlugins={[remarkGfm]}>{item.text}</Markdown>
                  </Box>
                </Flex>
              </Flex>
            )
          }
        })}
        {isLoading && <TypingIndicator />}
        {/* <AlwaysScrollToBottom /> */}
      </Flex>
    </>
  )
}

const InputMessage = ({
  inputMessage,
  setInputMessage,
  setIsLoading,
  isLoading,
  handleSendMessage,
  questionList,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [question, setQuestion] = React.useState('')

  const handleQuestion = async () => {
    await setInputMessage(question)
    await setIsOpen(false)
  }

  const closeQuestion = () => {
    setIsOpen(false)
  }

  const handleOpenDrawer = () => {
    setIsOpen(true)
  }

  return (
    <Flex w="100%" mt="4" mb="4">
      <Input
        variant="outline"
        placeholder="Ask me anything ? "
        _focus={{
          borderColor: 'brand.solid',
        }}
        onSubmit={(_e) => {
          handleSendMessage()
        }}
        value={inputMessage}
        onChange={(e) => setInputMessage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSendMessage()
            setIsLoading(true)
          }
        }}
      />

      <Button
        className="ml-4 home-button-hover"
        loading={isLoading}
        onClick={(_event) => {
          handleSendMessage()
          setIsLoading(true)
        }}
        px={8}
        ml="2"
        disabled={inputMessage.trim().length <= 0}
      >
        Send the question
      </Button>

      <Button className="ml-4" onClick={handleOpenDrawer} ml="2" px={8} colorPalette="orange">
        What can I ask ?
      </Button>

      <Drawer.Root placement="bottom" open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header borderBottomWidth="1px">Typical questions</Drawer.Header>
              <Drawer.Body>
                <RadioGroup.Root
                  defaultValue={question}
                  onValueChange={(e) => setQuestion(e.value)}
                >
                  <Stack direction="column" mb="4">
                    {questionList.map((q) => (
                      <RadioGroup.Item key={q} value={q}>
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemControl />
                        <RadioGroup.ItemText>{q}</RadioGroup.ItemText>
                      </RadioGroup.Item>
                    ))}
                  </Stack>
                </RadioGroup.Root>
                <Button className=" home-button-hover" onClick={handleQuestion} px={8}>
                  Use the selected question
                </Button>
                <Button className="ml-4" onClick={closeQuestion} px={8} colorPalette="orange">
                  Close
                </Button>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </Flex>
  )
}

const Chat = ({
  introMessage,
  promptinit,
  topics,
  questionList,
  isConnected,
  messagesPerSecond,
}) => {
  const [messages, setMessages] = useState([{ from: 'computer', text: introMessage }])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSendMessage = async () => {
    if (!inputMessage.trim().length) {
      return
    }
    setIsLoading(true)
    setMessages((old) => [...old, { from: 'me', text: inputMessage }])
    setInputMessage('')

    const fullPrompt = promptinit + JSON.stringify(topics)

    try {
      const response = await aiApi.gemini(fullPrompt, inputMessage)
      setMessages((old) => [...old, { from: 'computer', text: response.response }])
    } catch (error) {
      console.error('AI API error:', error)
      setMessages((old) => [
        ...old,
        {
          from: 'computer',
          text: 'Sorry there was an issue asking my brain, try again later :(',
        },
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const DownloadChat = (debugChat) => {
    const _today = new Date()
    let filename = 'chat.txt'
    let filecontent: string | number | boolean
    if (debugChat) {
      filename = 'debug.txt'
      filecontent = 'UNS BOT CHAT DEBUG\n'
      filecontent += '------------------------\n'
      filecontent += 'PROMPT INIT \n'
      filecontent += `${promptinit}\n`
      filecontent += '------------------------\n\n'
      filecontent += 'FULL CHAT' + '\n'
    } else {
      filecontent = 'UNS CHAT EXPORT\n'
      filecontent += '------------------------\n\n'
    }

    // biome-ignore lint/complexity/noForEach: <explanation>
    messages.forEach((message) => {
      filecontent += `From : ${message.from}\n`
      filecontent += `Text : ${message.text}\n`
    })

    const element = document.createElement('a')
    element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(filecontent)}`)
    element.setAttribute('download', filename)
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  const ResetChat = () => {
    setMessages([{ from: 'computer', text: introMessage }])
  }

  return (
    <Flex h="100%">
      <Flex h="100%" w="100%" flexDir="column">
        <Header isConnected={isConnected} messagesPerSecond={messagesPerSecond} />
        <Separator orientation="horizontal" />
        <Messages
          messages={messages}
          downloadChat={() => DownloadChat(false)}
          resetChat={ResetChat}
          debugChat={() => DownloadChat(true)}
          isLoading={isLoading}
        />
        <Separator orientation="horizontal" />
        <InputMessage
          questionList={questionList}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          handleSendMessage={handleSendMessage}
        />
      </Flex>
    </Flex>
  )
}

export default Chat
